export function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

type PendingResolver<T> = {
  resolve: (value: T | undefined) => void
  reject: (error: unknown) => void
}

export type BatchLoaderOptions<T> = {
  /** Fetches one batch of ids. An id missing from the result resolves `undefined`. */
  fetch: (ids: string[]) => Promise<Map<string, T>>
  /** How long to collect ids before the first fetch. */
  batchWindowMs: number
  /** The most ids one fetch may name; should match the endpoint's own cap. */
  maxBatchSize: number
  /** Maximum simultaneous fetches. Defaults to one. */
  maxConcurrentRequests?: number
}

/**
 * Batches single-id loads into fetches of up to `maxBatchSize` ids. Ids requested within
 * `batchWindowMs` of each other share a fetch. Fetches run serially unless concurrency is enabled.
 */
export function createBatchLoader<T>({
  fetch,
  batchWindowMs,
  maxBatchSize,
  maxConcurrentRequests = 1,
}: BatchLoaderOptions<T>): {
  load: (id: string) => Promise<T | undefined>
} {
  if (!Number.isSafeInteger(maxConcurrentRequests) || maxConcurrentRequests < 1) {
    throw new RangeError('maxConcurrentRequests must be a positive integer')
  }

  const pending = new Map<string, Array<PendingResolver<T>>>()
  let scheduled = false
  let activeRequests = 0

  function scheduleDrain(): void {
    // When all slots are occupied, a completion will pick up the queued ids.
    if (scheduled || activeRequests >= maxConcurrentRequests) return
    scheduled = true
    setTimeout(() => {
      scheduled = false
      drain()
    }, batchWindowMs)
  }

  function drain(): void {
    if (scheduled) return
    while (pending.size > 0 && activeRequests < maxConcurrentRequests) {
      const batch = new Map<string, Array<PendingResolver<T>>>()
      for (const [id, resolvers] of pending) {
        batch.set(id, resolvers)
        pending.delete(id)
        if (batch.size >= maxBatchSize) break
      }
      activeRequests += 1
      void fetchBatch(batch)
    }
  }

  async function fetchBatch(batch: Map<string, Array<PendingResolver<T>>>): Promise<void> {
    try {
      const byId = await fetch([...batch.keys()])
      for (const [id, resolvers] of batch) {
        for (const resolver of resolvers) resolver.resolve(byId.get(id))
      }
    } catch (error) {
      // A failed batch rejects only its callers and still releases its slot.
      for (const resolvers of batch.values()) {
        for (const resolver of resolvers) resolver.reject(error)
      }
    } finally {
      activeRequests -= 1
      drain()
    }
  }

  return {
    load(id: string): Promise<T | undefined> {
      return new Promise<T | undefined>((resolve, reject) => {
        const resolvers = pending.get(id) ?? []
        resolvers.push({resolve, reject})
        pending.set(id, resolvers)
        scheduleDrain()
      })
    },
  }
}
